/**
 * Beschreibung und Personalisierung als zwei Felder nebeneinander.
 *
 * Im Kauffenster standen beide untereinander, jedes mit eigener Zeile und
 * eigenem Pfeil. Auf dem Handy kostet das zwei Zeilen fuer zwei Woerter.
 *
 * Stattdessen eine Zeile mit zwei Feldern: ein Tippen oeffnet den zugehoerigen
 * Text, das jeweils andere Feld schliesst sich dabei. Das geoeffnete Feld ist
 * farblich hinterlegt, ein zweites Tippen schliesst es wieder.
 *
 * Die Aufklappfelder selbst bleiben, wie sie sind - nur ihre Kopfzeile wird
 * ausgeblendet und durch diese Felder ersetzt. So bleiben das Aufgehen, die
 * Formularfelder der Personalisierung und alles andere unangetastet; gesteuert
 * wird nur das offene oder geschlossene <details>.
 *
 * Die Zeile entsteht auch dann, wenn es nur ein Aufklappfeld gibt - es nimmt
 * dann die volle Breite ein. So sieht ein Artikel ohne Personalisierung nicht
 * anders aus als einer mit.
 */
const FENSTER = '.dp-kauffenster';

/*
 * Die Reiterzeile ist eine Loesung fuers Handy. Am Rechner stehen Beschreibung
 * und Personalisierung immer offen (dp-kauffenster.js haelt sie so) - dort darf
 * kein Handgriff dieser Datei sie schliessen.
 */
const SCHMAL = window.matchMedia('(max-width: 749px)');

/** Kennzeichnet das Fenster, sobald die Zeile steht - siehe Stilblatt. */
const BEREIT = 'dp-kauffenster--reiter';

/**
 * Steht in dem Aufklappfeld ueberhaupt etwas?
 *
 * Ohne Beschreibung gibt das Theme das Feld trotzdem aus - leer. Dann soll
 * auch kein Reiter dafuer entstehen; sonst fuehrt ein Tippen ins Nichts.
 * Geprueft wird auf Elemente, nicht auf Text: der Inhalt kommt als Auszeichnung
 * aus dem Adminbereich.
 *
 * @param {HTMLDetailsElement} feld
 * @returns {boolean}
 */
function hatInhalt(feld) {
  const inhalt = feld.querySelector('.details-content');
  return !!inhalt && inhalt.children.length > 0;
}

/**
 * @param {HTMLDetailsElement} feld
 * @returns {string}
 */
function beschriftung(feld) {
  const kopf = feld.querySelector('summary');
  if (!kopf) return '';

  /*
   * Im Kopf steht neben dem Wort noch das Zeichen zum Aufklappen. Gesucht ist
   * nur der Text, also werden die Zeichen uebergangen.
   */
  return [...kopf.childNodes]
    .filter((knoten) => knoten.nodeType === 3 || !knoten.querySelector?.('svg'))
    .map((knoten) => knoten.textContent)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Der Abstand zwischen Reiterzeile und Text - dieselben 6 Pixel wie im Stilblatt. */
const LUFT_UEBER_TEXT = 6;

/**
 * Wie hoch der offene Reiter hoechstens werden darf - oder null, solange
 * sich nichts messen laesst.
 *
 * Gemessen wird der Platz, den der Lauf dem Text laesst: von der Unterkante
 * der Reiterzeile bis zu seiner eigenen Unterkante. Der Lauf teilt sich den
 * Platz im Fenster mit Optionsauswahl und Kaufzeile und schrumpft, wenn es
 * eng wird - seine Unterkante ist damit ein ehrliches Mass, unabhaengig
 * davon, wie hoch der Text gerade steht.
 *
 * Frueher stand hier die Differenz aus Fensterhoehe und Texthoehe. Lief der
 * Text ueber das Fenster hinaus - mit offener Tastatur war das der Regelfall -,
 * bestaetigte diese Rechnung den zu grossen Wert immer wieder, und der Text
 * lag hinter Farbreihe, Mengenzaehler und Kaufzeile.
 *
 * @param {HTMLElement} fenster
 * @param {HTMLDetailsElement[]} felder
 * @returns {number | null}
 */
function raumFuerText(fenster, felder) {
  if (gemerkterRaum !== null) return gemerkterRaum;

  /*
   * Nur aus ruhigem Stand: Steht das Fenster als Minileiste (60 Pixel) oder
   * faehrt es von dort gerade auf, ist sein Kasten kein Mass fuer den Platz
   * - und genau dann kam die Messung schon vor: Die Pflichtpruefung oeffnet
   * den Reiter, waehrend das Fenster von der Leiste aus noch waechst. Aus
   * 60 Pixeln "ohne Text" wurde ein Platz, der den ganzen Bildschirm
   * fuellte, und der Reiter lief hinter die Kaufzeile. Solange es unruhig
   * ist, bleibt es beim Vorgabewert des Stilblatts; nachgemessen wird,
   * sobald die Fahrt vorbei ist.
   */
  if (istUnruhig(fenster)) {
    messeNach(fenster, felder);
    return null;
  }

  const grenze = Number.parseFloat(getComputedStyle(fenster).maxHeight);
  if (!grenze) return null;

  const lauf = fenster.querySelector('.dp-kauffenster__lauf');
  const zeile = fenster.querySelector('.dp-reiter');
  if (!lauf || !zeile) return null;

  /*
   * Klebt das Fenster an seiner Grenze, ist der Platz knapp - und dann sagt
   * die Differenz aus Fenster- und Texthoehe nichts mehr: sie bestaetigt den
   * zu grossen Wert immer wieder, denn das Fenster waechst ja nicht mit.
   * Gemessen wird stattdessen der Lauf selbst. Er teilt sich den Platz im
   * Fenster mit Optionsauswahl und Kaufzeile und ist dann wirklich
   * geschrumpft; was unter der Reiterzeile bleibt, ist der Platz fuer den Text.
   */
  const hoehe = fenster.getBoundingClientRect().height;
  if (hoehe >= grenze - 1) {
    const frei = lauf.getBoundingClientRect().bottom - zeile.getBoundingClientRect().bottom - LUFT_UEBER_TEXT;
    gemerkterRaum = Math.max(0, Math.round(frei));
    return gemerkterRaum;
  }

  /*
   * Sonst passt es: Was das Fenster ohne den Text misst, ist sein fester
   * Teil - der Rest bis zur Grenze gehoert dem Text.
   */
  let belegt = 0;
  for (const feld of felder) {
    const text = feld.querySelector('.details-content');
    if (feld.open && text) belegt += text.getBoundingClientRect().height;
  }

  // Ganz ohne Platz waere der Text unlesbar - unter 120 Pixel geht es nicht.
  gemerkterRaum = Math.max(120, Math.round(grenze - (hoehe - belegt)));
  return gemerkterRaum;
}

/** Ob das Fenster gerade als Leiste steht oder seine Hoehe faehrt. */
function istUnruhig(fenster) {
  return (
    fenster.classList.contains('dp-kauffenster--klein') ||
    fenster.classList.contains('dp-kauffenster--faehrt')
  );
}

/**
 * Misst den Platz nach, sobald das Fenster zur Ruhe gekommen ist, und
 * schreibt ihn an das Fenster. Die Fahrt dauert 380 Millisekunden
 * (dp-kauffenster.js); geprueft wird in etwas groesseren Abstaenden, bis
 * zu fuenfmal.
 *
 * @param {HTMLElement} fenster
 * @param {HTMLDetailsElement[]} felder
 */
let nachmessung = 0;

function messeNach(fenster, felder, versuch = 0) {
  window.clearTimeout(nachmessung);
  nachmessung = window.setTimeout(() => {
    if (istUnruhig(fenster)) {
      if (versuch < 5) messeNach(fenster, felder, versuch + 1);
      return;
    }
    if (ziehtGerade()) return;
    const raum = raumFuerText(fenster, felder);
    if (raum !== null) fenster.style.setProperty('--dp-inhalt-max', raum + 'px');
  }, 450);
}

/**
 * Ob gerade an der Pille gezogen wird.
 *
 * Waehrend des Zugs fuehrt der Finger das Hoechstmass des Textes. Ein
 * Nachmessen wuerde es im selben Augenblick ueberschreiben - und genau das
 * geschah unter iOS: Kaum bewegt sich der Finger, faehrt Safaris
 * Adressleiste ein, das meldet sich als Groessenwechsel des sichtbaren
 * Fensters, und sechzig Millisekunden spaeter stand wieder der gerechnete
 * Wert da. Die Geste lief also vollstaendig durch und wurde Bild fuer Bild
 * zurueckgenommen - sichtbar blieb nichts. Unter Android faehrt bei so
 * einer kleinen Bewegung keine Leiste ein, deshalb fiel es dort nie auf.
 */
function ziehtGerade() {
  return !!document.querySelector('.dp-kauffenster--zieht');
}

/** Einmal gemessen, dann gemerkt - null heisst: noch nicht gemessen. Siehe raumFuerText. */
let gemerkterRaum = null;

/*
 * Tastatur, Drehung, andere Fenstergroesse: im Kauffenster ist dann ein
 * anderer Platz. Der einmal gemerkte Wert galt weiter, und mit offener
 * Tastatur stand der Reiter deshalb in alter Hoehe da - sein Inhalt lief
 * hinter Farbreihe, Mengenzaehler und Kaufzeile.
 *
 * Der gemerkte Wert faellt weg, und bis zur frischen Messung gilt der
 * Vorgabewert des Stilblatts (40svh) - der schrumpft mit der Tastatur von
 * selbst mit, also laeuft in der Zwischenzeit nichts ueber.
 */
function vermesseNeu() {
  const fenster = document.querySelector(FENSTER);
  if (!fenster) return;
  const lauf = fenster.querySelector('.dp-kauffenster__lauf') || fenster;
  const felder = [...lauf.querySelectorAll('details')].filter(hatInhalt);
  if (!felder.length) return;
  /*
   * Gemessen wird sofort, nicht erst nach der Ruhepause: die Tastatur faehrt
   * schnell auf, und bis dahin stuende der Text zu hoch da. Die Nachmessung
   * holt danach den ruhigen Stand.
   */
  setzePlatz(fenster, felder);
  messeNach(fenster, felder);
}

/**
 * Misst den Platz frisch und schreibt ihn ans Fenster.
 *
 * @param {HTMLElement} fenster
 * @param {HTMLDetailsElement[]} felder
 */
function setzePlatz(fenster, felder) {
  if (ziehtGerade()) return;
  gemerkterRaum = null;
  const raum = raumFuerText(fenster, felder);
  if (raum !== null) fenster.style.setProperty('--dp-inhalt-max', raum + 'px');
}

let groessenUhr = 0;

function beiGroessenwechsel() {
  // Die Reiterhoehe gilt nur am Handy - am Rechner rollt der Lauf selbst.
  if (!window.matchMedia('(max-width: 749px)').matches) return;
  /* Waehrend des Zugs fuehrt der Finger - siehe ziehtGerade. */
  if (ziehtGerade()) return;
  window.clearTimeout(groessenUhr);
  groessenUhr = window.setTimeout(vermesseNeu, 60);
}

window.addEventListener('resize', beiGroessenwechsel, { passive: true });
window.addEventListener('orientationchange', beiGroessenwechsel, { passive: true });
/*
 * Die Tastatur meldet sich nicht immer als Fenster-Groessenwechsel - der
 * sichtbare Ausschnitt kennt sie aber in jedem Fall.
 */
if (window.visualViewport) window.visualViewport.addEventListener('resize', beiGroessenwechsel, { passive: true });

/** Haelt die alten Kopfzeilen zurueck, bis die Reiterzeile steht. */
const WARTET = 'dp-reiter-wartet';

function starte() {
  /*
   * Die Marke faellt in jedem Fall wieder ab - auch wenn hier nichts gebaut
   * wird. Sonst blieben die Kopfzeilen fuer immer verborgen und das
   * Aufklappfeld waere nicht mehr zu oeffnen.
   */
  try {
    baue();
  } finally {
    document.documentElement.classList.remove(WARTET);
  }
}

function baue() {
  const fenster = document.querySelector(FENSTER);
  if (!fenster) return;

  const lauf = fenster.querySelector('.dp-kauffenster__lauf') || fenster;
  const alleFelder = [...lauf.querySelectorAll('details')];

  /*
   * Leere Felder tragen den Zustand als Klasse - das Stilblatt versteckte
   * sie frueher ueber :has(), und die Selektorform machte die
   * Reiter-Animation auf dem Handy teuer. Der Nachzuegler-Neubau haelt die
   * Klasse aktuell, sobald IMPRINT seine Felder eingetragen hat.
   */
  for (const feld of alleFelder) {
    feld.classList.toggle('dp-feld-leer', !hatInhalt(feld));
  }

  const felder = alleFelder.filter(hatInhalt);
  if (!felder.length) return;

  const kopf = document.createElement('div');
  kopf.className = 'dp-reiter';

  /*
   * Die Zeile steht dort, wo bisher das erste Aufklappfeld begann. Gesucht ist
   * dessen aeusserster Kasten innerhalb des Laufs - die Felder stecken je nach
   * Herkunft unterschiedlich tief in Huellen.
   */
  let anker = felder[0];
  while (anker.parentElement && anker.parentElement !== lauf) anker = anker.parentElement;
  anker.before(kopf);

  /** @type {HTMLButtonElement[]} */
  const knoepfe = [];

  felder.forEach((feld) => {
    const wort = beschriftung(feld);
    if (!wort) return;

    const knopf = document.createElement('button');
    knopf.type = 'button';
    knopf.className = 'dp-reiter__knopf';
    knopf.setAttribute('aria-expanded', feld.open ? 'true' : 'false');

    /*
     * Das Wort steht in einer eigenen Huelle, damit es sich neben dem Pfeil
     * kuerzen laesst - "Personalisierung" ist lang, der Platz ist es nicht.
     */
    const marke = document.createElement('span');
    marke.className = 'dp-reiter__wort';
    marke.textContent = wort;
    knopf.appendChild(marke);

    /*
     * Der Pfeil sagt, dass sich hier etwas aufklappen laesst - derselbe wie am
     * Rabattcode und am Feld "personalisiert" im Warenkorb. Zugeklappt zeigt er
     * nach unten, aufgeklappt nach oben; gedreht wird im Stylesheet, es liest
     * dafuer aria-expanded.
     */
    const pfeil = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    pfeil.setAttribute('class', 'dp-korb-pfeil dp-reiter__pfeil');
    pfeil.setAttribute('viewBox', '0 0 24 24');
    pfeil.setAttribute('aria-hidden', 'true');
    pfeil.setAttribute('focusable', 'false');

    const strich = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    strich.setAttribute('d', 'M9 5l7 7-7 7');
    strich.setAttribute('fill', 'none');
    strich.setAttribute('stroke', 'currentColor');
    strich.setAttribute('stroke-width', '2.2');
    strich.setAttribute('stroke-linecap', 'round');
    strich.setAttribute('stroke-linejoin', 'round');
    pfeil.appendChild(strich);
    knopf.appendChild(pfeil);

    /*
     * Der Reiter der Personalisierung dreht seinen Pfeil immer im
     * Uhrzeigersinn - siehe dreheImUhrzeigersinn. Erkannt wird er am Feld, das
     * an ihm haengt, nicht am Wort: das Wort kommt aus den Einstellungen und
     * kann sich aendern. An der Klasse des Knopfes haengt auch die
     * Pflichtpruefung: sie oeffnet den Reiter darueber, wenn beim Kauf eine
     * Angabe fehlt (dp-personalisierung.js).
     */
    if (feld.querySelector('.dp-perso, [data-dp-perso]')) {
      knopf.classList.add('dp-reiter__knopf--perso');
      dreheImUhrzeigersinn(knopf, feld, fenster);
    }

    knopf.addEventListener('click', () => {
      const oeffnen = !feld.open;

      /*
       * Der Platz steht fest, bevor sich etwas bewegt - danach waeren die
       * Masse mitten im Zusammenfahren abgenommen. Er gilt fuer beide Felder
       * und haengt deshalb am Fenster, nicht am einzelnen Text.
       */
      const raum = raumFuerText(fenster, felder);
      // Kein Mass (Fenster unruhig): der Vorgabewert des Stilblatts gilt, bis nachgemessen ist.
      if (raum !== null) fenster.style.setProperty('--dp-inhalt-max', raum + 'px');
      else fenster.style.removeProperty('--dp-inhalt-max');

      // Erst alles zu - dann das gewaehlte auf. So ist immer hoechstens eines offen.
      for (const anderes of felder) anderes.open = false;

      feld.open = oeffnen;

      /*
       * Und wenn der Text steht, noch einmal nachmessen.
       *
       * Vor dem Aufgehen fehlen im Fenster die Abstaende, die erst mit dem
       * Text entstehen - der Abstand zur Reiterzeile und die Luecke zur
       * Kaufzeile. Der Platz kam dadurch rund zwanzig Pixel zu gross heraus,
       * und genau so weit lief der Text hinter Farbreihe, Mengenzaehler und
       * Kaufzeile. Gemessen mit offenem Text stimmt die Rechnung.
       */
      if (oeffnen) {
        window.setTimeout(() => {
          if (feld.open) setzePlatz(fenster, felder);
        }, 700);
      }

      /*
       * Die Marke sagt dp-kauffenster.js, dass diese Wahl vom Nutzer kommt.
       * Ohne sie klappte richteBeschreibung das Feld beim naechsten Umbau
       * wieder zu - seit Felder nachtraeglich einziehen (IMPRINT), passiert
       * so ein Umbau auch mitten in der Nutzung.
       */
      feld.dataset.dpVomNutzer = '';

      zeigeZustand();
    });

    kopf.appendChild(knopf);
    knoepfe.push(knopf);

    /*
     * Auch von aussen gesteuerte Wechsel sollen ankommen - am Rechner klappt
     * das Fenster die Beschreibung selbst auf (siehe dp-kauffenster.js).
     */
    feld.addEventListener('toggle', zeigeZustand);
  });

  function zeigeZustand() {
    felder.forEach((feld, i) => {
      const knopf = knoepfe[i];
      if (knopf) knopf.setAttribute('aria-expanded', feld.open ? 'true' : 'false');
    });
  }

  /*
   * Die Grossansicht eines Bildes schliesst die Felder - beim Aufgehen wie
   * beim Zugehen. Wer aus einem Bild zurueckkommt, soll das Fenster in seinem
   * ruhigen Zustand vorfinden und nicht einen Text, der noch von vorhin offen
   * steht.
   *
   * dp-bild-overlay.js meldet den Zustand am Dokument; darauf zu hoeren ist
   * verlaesslicher, als die Bilder selbst zu beobachten - die Grossansicht
   * laesst sich auch ueber die Tastatur oder einen Verweis oeffnen.
   */
  new MutationObserver((meldungen) => {
    /*
     * Die Bilder einer Bewertung lassen das Kauffenster in Ruhe (Wert
     * 'bewertung', dp-bild-overlay.js) - beim Oeffnen wie beim Schliessen.
     */
    const m = meldungen[meldungen.length - 1];
    if (document.documentElement.dataset.dpBildfeld === 'bewertung' || (m && m.oldValue === 'bewertung')) return;
    /*
     * Nur auf dem Handy: am Rechner stehen die Texte immer offen, und das
     * Zuklappen hier - mit dem sofortigen Wiederaufklappen durch
     * dp-kauffenster.js - liess das Kauffenster beim Oeffnen der Grossansicht
     * sichtbar zucken.
     */
    if (!SCHMAL.matches) return;

    for (const feld of felder) feld.open = false;
    zeigeZustand();
  }).observe(document.documentElement, { attributes: true, attributeOldValue: true, attributeFilter: ['data-dp-bildfeld'] });

  /*
   * Der Griff sucht live statt ueber die Liste dieses Aufbaus: die
   * IMPRINT-Felder ziehen nachtraeglich ein, der Nachzuegler baut die Reiter
   * dann neu - der Griff aber bleibt der erste (baueGriff kehrt bei
   * vorhandenem Griff sofort um) und behielte die alte Liste. Auf der
   * IMPRINT-Vorlage fehlte darin das Personalisierungs-Feld, weil es beim
   * ersten Aufbau noch leer war: Ziehen und Tipp-Schliessen liefen ins
   * Leere, sobald es offen stand - die Beschreibung ging weiter.
   */
  baueGriff(
    fenster,
    () =>
      [...fenster.querySelectorAll('details')]
        .find((feld) => feld.open && hatInhalt(feld))
        ?.querySelector('.details-content') || null,
    () => {
      for (const feld of fenster.querySelectorAll('details')) feld.open = false;
      zeigeZustand();
    }
  );

  fenster.classList.add(BEREIT);
  zeigeZustand();
}

/**
 * Laesst den Pfeil eines Reiters stets im Uhrzeigersinn drehen.
 *
 * Ueber das Stylesheet allein geht das nicht: dort steht je Zustand ein
 * Winkel, und der Browser nimmt zwischen zwei Winkeln immer den kuerzeren Weg.
 * Beim Aufklappen drehte der Pfeil deshalb rechtsherum, beim Zuklappen wieder
 * zurueck.
 *
 * Hier zaehlt stattdessen ein Winkel hoch: jeder Wechsel der Stellung dreht
 * ihn um genau 180 Grad weiter. Zwei Dinge bestimmen die Stellung - ob der
 * Text offen ist und ob das Fenster an der Bildkante eingerastet ist.
 *
 * Nie mehr als eine halbe Umdrehung auf einmal. Beim Andocken rastet das
 * Fenster innerhalb von Sekundenbruchteilen mehrmals ein und aus; jeder
 * dieser Wechsel war eine eigene halbe Umdrehung, und der Pfeil drehte
 * anderthalb Runden (auf Jimmis Handy gefilmt: unten, oben, unten, links,
 * oben in gut 0,2 Sekunden). Deshalb:
 *
 * - Ein Wechsel des Einrastens gilt erst, wenn er kurz Bestand hat
 *   (BERUHIGT) - Flattern kommt so gar nicht erst an.
 * - Waehrend eine Drehung laeuft, beginnt keine neue. Danach wird einmal
 *   nachgesehen: Steht der Pfeil schon richtig, bleibt er stehen, sonst folgt
 *   genau eine halbe Umdrehung.
 * - Auf- und Zuklappen dreht sofort - das ist die Geste des Kunden.
 * - Im Hintergrund (Browser minimiert) wird ohne Drehung gestellt, beim
 *   Zurueckkehren ebenso - dort holt der Browser sonst alles auf einmal nach.
 *
 * Der Winkel steht als Merkmal am Knopf, das Stylesheet liest ihn. Laeuft das
 * Skript nicht, greift der dort hinterlegte Ersatzwert und der Pfeil verhaelt
 * sich wie die uebrigen.
 *
 * @param {HTMLElement} knopf
 * @param {HTMLDetailsElement} feld
 * @param {HTMLElement} fenster
 */
function dreheImUhrzeigersinn(knopf, feld, fenster) {
  /** So lange muss das Einrasten Bestand haben, bevor der Pfeil ihm folgt. */
  const BERUHIGT = 120;

  const pfeil = knopf.querySelector('.dp-korb-pfeil');
  const geparkt = () => fenster.classList.contains('dp-kauffenster--geparkt');
  /** Die Stellung, die der Pfeil zeigen soll: 0 oder 1. */
  const soll = () => ((feld.open ? 1 : 0) + (geparkt() ? 1 : 0)) % 2;

  let gezeigt = soll();
  let winkel = gezeigt === 1 ? 90 : -90;
  let drehtBis = 0;
  let uhr = 0;
  let dauer = 0;
  let warGeparkt = geparkt();

  const dauerJetzt = () => {
    if (!dauer && (pfeil instanceof SVGElement || pfeil instanceof HTMLElement)) {
      const wert = String(getComputedStyle(pfeil).transitionDuration).split(',')[0].trim();
      dauer = wert.endsWith('ms') ? parseFloat(wert) : parseFloat(wert) * 1000;
    }
    return Number.isFinite(dauer) && dauer > 0 ? dauer : 180;
  };

  /* Ohne Drehung einstellen - beim Aufbau, im Hintergrund und bei der Rueckkehr. */
  const ohneDrehung = () => {
    drehtBis = 0;
    if (!(pfeil instanceof SVGElement || pfeil instanceof HTMLElement)) {
      knopf.style.setProperty('--dp-pfeil-drehung', winkel + 'deg');
      return;
    }
    pfeil.style.transition = 'none';
    knopf.style.setProperty('--dp-pfeil-drehung', winkel + 'deg');
    void getComputedStyle(pfeil).rotate;
    requestAnimationFrame(() => requestAnimationFrame(() => pfeil.style.removeProperty('transition')));
  };

  const pruefe = () => {
    window.clearTimeout(uhr);
    const jetzt = performance.now();
    // Eine Drehung laeuft noch - erst danach wird nachgesehen.
    if (jetzt < drehtBis) {
      uhr = window.setTimeout(pruefe, drehtBis - jetzt + 20);
      return;
    }
    const stellung = soll();
    if (stellung === gezeigt) return;
    gezeigt = stellung;
    winkel += 180;
    if (document.hidden) {
      ohneDrehung();
      return;
    }
    drehtBis = jetzt + dauerJetzt();
    knopf.style.setProperty('--dp-pfeil-drehung', winkel + 'deg');
  };

  ohneDrehung();

  // Auf- und Zuklappen: sofort.
  feld.addEventListener('toggle', pruefe);

  // Einrasten: erst, wenn es Bestand hat. Andere Klassen des Fensters aendern die Stellung nicht.
  new MutationObserver(() => {
    const jetzt = geparkt();
    if (jetzt === warGeparkt) return;
    warGeparkt = jetzt;
    window.clearTimeout(uhr);
    uhr = window.setTimeout(pruefe, BERUHIGT);
  }).observe(fenster, {
    attributes: true,
    attributeFilter: ['class'],
  });

  /*
   * Zurueck aus dem Hintergrund: sofort in der richtigen Stellung, ohne die
   * aufgelaufenen Wechsel nachzudrehen; der Winkel wird dabei klein gehalten.
   */
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    window.clearTimeout(uhr);
    gezeigt = soll();
    warGeparkt = geparkt();
    winkel = gezeigt === 1 ? 90 : -90;
    ohneDrehung();
  });
}

/** Ab dieser Strecke nach unten gilt die Geste als Wischen. */
const WISCHWEG = 28;

/**
 * Setzt den Griff an den oberen Rand des Fensters.
 *
 * Er sieht aus wie der Streifen am unteren Rand eines iPhones und meint
 * dasselbe: hier laesst sich das, was gerade offen ist, wieder wegschieben.
 * Sichtbar wird er nur, solange ein Text offen steht - siehe Stilblatt.
 *
 * Beim Ziehen folgt die Oberkante des Fensters dem Finger. Moeglich ist das,
 * weil das Fenster unten festsitzt und nach oben mit seinem Inhalt waechst:
 * nimmt man dem offenen Text Hoehe weg, sinkt seine Oberkante um genau diesen
 * Betrag. Waehrend der Geste laeuft dabei keine Bewegung mit - der Rand soll
 * am Finger haengen und nicht hinterherlaufen.
 *
 * Reicht der Weg nicht, faehrt der Text wieder auf seine Hoehe; reicht er,
 * schliesst sich das Feld von der Stelle aus, an der der Finger losgelassen
 * wurde.
 *
 * @param {HTMLElement} fenster
 * @param {() => HTMLElement | null} offenerText
 * @param {() => void} schliesse
 */
function baueGriff(fenster, offenerText, schliesse) {
  if (fenster.querySelector('.dp-kauffenster__griff')) return;

  const griff = document.createElement('button');
  griff.type = 'button';
  griff.className = 'dp-kauffenster__griff';
  griff.setAttribute('aria-label', 'Schließen');
  fenster.prepend(griff);

  let start = 0;
  let zeiger = -1;
  /** Kennung des Fingers, wenn die Geste ueber Beruehrungsereignisse laeuft. */
  let beruehrung = -1;
  let gezogen = false;
  let hoehe = 0;
  let voll = '';

  /** Merkt sich, wovon aus gezogen wird. */
  const beginne = (klientY) => {
    const text = offenerText();
    if (!text) {
      return false;
    }
    start = klientY;
    gezogen = false;

    // Von dieser Hoehe aus wird gerechnet - und auf sie faellt es zurueck.
    hoehe = text.getBoundingClientRect().height;
    voll = fenster.style.getPropertyValue('--dp-inhalt-max');

    fenster.classList.add('dp-kauffenster--zieht');
    return true;
  };

  griff.addEventListener('pointerdown', (ereignis) => {
    // Der Finger fuehrt bereits ueber die Beruehrungsereignisse.
    if (beruehrung >= 0) return;
    if (!beginne(ereignis.clientY)) return;

    /*
     * Hier wird nichts abgesagt.
     *
     * Ein abgesagtes pointerdown nimmt unter WebKit wie unter Blink die ganze
     * Beruehrungsfolge mit: touchstart und touchmove bleiben aus. Genau ueber
     * die laeuft aber der zweite Weg, den es fuer iOS gibt - und damit war das
     * Ziehen dort tot, waehrend der Klick zum Schliessen weiter ankam. Das
     * Rollen der Seite haelt touch-action: none zurueck, Safaris eigene Geste
     * das preventDefault im touchmove.
     */
    zeiger = ereignis.pointerId;

    /*
     * Das Einfangen des Zeigers haelt die Geste beim Griff, auch wenn der
     * Finger darueber hinausrutscht. Ob es gelingt, entscheidet der Browser -
     * die Geste darf davon nicht abhaengen, deshalb wird der Zustand hier
     * selbst gefuehrt.
     */
    try {
      griff.setPointerCapture(ereignis.pointerId);
    } catch {
      /* ohne Einfangen geht es auch */
    }

  });

  griff.addEventListener('pointermove', (ereignis) => {
    if (zeiger < 0 || ereignis.pointerId !== zeiger) return;

    // Nur nach unten - nach oben gibt es nichts zu schieben.
    folge(ereignis.clientY);
  });

  /**
   * Beendet die Geste an der uebergebenen Fingerhoehe - gleich, ob die
   * Meldung als Zeiger- oder als Beruehrungsereignis kam.
   *
   * @param {number} klientY
   */
  const schliesseGeste = (klientY) => {
    zeiger = -1;
    fenster.classList.remove('dp-kauffenster--zieht');

    /* Das Mass am Element gilt nur waehrend des Zugs - danach fuehrt wieder das Fenster. */
    const gezogenerText = offenerText();
    if (gezogenerText) gezogenerText.style.removeProperty('max-height');

    if (klientY - start >= WISCHWEG) {
      /*
       * Geschlossen wird von der erreichten Hoehe aus. Der volle Wert kommt
       * beim naechsten Oeffnen ohnehin zurueck - ihn jetzt zu setzen liesse
       * den Text noch einmal aufspringen, bevor er verschwindet.
       */
      schliesse();
      return;
    }

    fenster.style.setProperty('--dp-inhalt-max', voll);
  };

  /** Zieht den Rand an die Fingerhoehe. */
  const folge = (klientY) => {
    const weg = Math.max(0, Math.min(hoehe, klientY - start));
    if (weg > 4) gezogen = true;

    fenster.style.setProperty('--dp-inhalt-max', Math.round(hoehe - weg) + 'px');

    /*
     * Und dasselbe Mass noch einmal direkt am Text.
     *
     * Die Variable allein genuegte nicht: Das Fenster misst den Platz selbst
     * nach und schreibt in dieselbe Variable - unter iOS bei jeder Bewegung,
     * weil dort schon das Wischen Safaris Leiste ein- und ausfahren laesst.
     * Die Geste zog, das Nachmessen zog zurueck. Ein Mass am Element selbst
     * sticht jede Regel aus dem Stilblatt und jede Variable; wer auch immer
     * dazwischenschreibt, kommt daran nicht mehr vorbei. Beim Loslassen
     * faellt es wieder weg.
     */
    const gezogenerText = offenerText();
    if (gezogenerText) {
      gezogenerText.style.setProperty('max-height', Math.round(hoehe - weg) + 'px', 'important');
    }

  };

  const beende = (ereignis) => {
    if (zeiger < 0 || ereignis.pointerId !== zeiger) return;

    try {
      griff.releasePointerCapture(ereignis.pointerId);
    } catch {
      /* war nie eingefangen */
    }

    /*
     * Bricht Safari die Zeigerereignisse ab (pointercancel), weil es die
     * Beruehrung selbst deuten will, laeuft die Geste ueber die
     * Beruehrungsereignisse weiter. Dann endet hier nur der Zeigerpfad -
     * Hoehe und Zustand bleiben in der Hand des Fingers.
     */
    if (beruehrung >= 0) {
      zeiger = -1;
      return;
    }

    schliesseGeste(ereignis.clientY);
  };

  griff.addEventListener('pointerup', beende);
  griff.addEventListener('pointercancel', beende);

  /*
   * Dieselben Horcher noch einmal am Fenster: waehrend der Geste sinkt die
   * Oberkante des Kauffensters, und der Griff wandert mit ihr unter dem
   * stehenden Finger weg. Haelt das Einfangen des Zeigers nicht - auf iOS
   * kommt das vor -, meldet der Griff selbst dann nichts mehr, und die Geste
   * risse ab. Die Fensterhorcher fangen das auf; doppelt verarbeitet wird
   * nichts, die Kennung des Zeigers laesst jedes Ereignis nur einmal zu.
   */
  window.addEventListener('pointermove', (ereignis) => {
    if (zeiger < 0 || ereignis.pointerId !== zeiger) return;

    folge(ereignis.clientY);
  }, { passive: true });

  window.addEventListener('pointerup', beende);
  window.addEventListener('pointercancel', beende);

  /*
   * Beruehrungsereignisse als zweiter Weg fuer iOS.
   *
   * Dort blieb die Geste stumm: nach dem Druecken kamen keine
   * Zeigerbewegungen mehr an - Safari macht aus dem Ziehen eine eigene
   * Browsergeste, trotz touch-action. Die Beruehrungsereignisse kommen
   * trotzdem, und ihr preventDefault ist das einzige Mittel, das Safari
   * verlaesslich davon abhaelt. Der Zustand (start, hoehe, voll) kommt
   * weiterhin aus pointerdown, das auch auf iOS feuert; wo die
   * Zeigerbewegungen funktionieren, aendern die doppelten Meldungen nichts -
   * beide setzen dieselbe Hoehe.
   */
  griff.addEventListener(
    'touchstart',
    (ereignis) => {
      if (beruehrung >= 0) return;

      const finger = ereignis.changedTouches[0];

      /*
       * Der Beruehrungspfad beginnt selbst, statt sich auf pointerdown zu
       * verlassen: Safari bricht die Zeigerereignisse mit pointercancel ab,
       * sobald es die Beruehrung als eigene Geste deutet - der Zeigerpfad
       * ist dann schon wieder beendet, wenn der Finger erst richtig zieht.
       *
       * Kein preventDefault an dieser Stelle: es wuerde den Klick
       * unterbinden, mit dem sich der Griff antippen laesst.
       */
      if (!beginne(finger.clientY)) return;

      beruehrung = finger.identifier;
    },
    { passive: true }
  );

  /** @param {TouchList} liste */
  const meinFinger = (liste) => {
    for (const finger of liste) {
      if (finger.identifier === beruehrung) return finger;
    }
    return null;
  };

  griff.addEventListener(
    'touchmove',
    (ereignis) => {
      if (beruehrung < 0) return;
      const finger = meinFinger(ereignis.changedTouches);
      if (!finger) return;

      folge(finger.clientY);

      /*
       * Das einzige Mittel, das Safari verlaesslich davon abhaelt, aus dem
       * Ziehen ein Scrollen zu machen. Erst hier und nicht beim Aufsetzen:
       * ein blosses Tippen soll ein Klick bleiben.
       */
      ereignis.preventDefault();
    },
    { passive: false }
  );

  const beruehrungEnde = (ereignis) => {
    if (beruehrung < 0) return;
    const finger = meinFinger(ereignis.changedTouches);
    if (!finger) return;

    beruehrung = -1;

    // Der Zeigerpfad ist entweder laengst abgebrochen oder endet parallel.
    if (zeiger >= 0) {
      zeiger = -1;
      schliesseGeste(finger.clientY);
      return;
    }

    schliesseGeste(finger.clientY);
  };

  griff.addEventListener('touchend', beruehrungEnde);
  griff.addEventListener('touchcancel', beruehrungEnde);

  /*
   * Ein Tippen schliesst ebenfalls - das erwartet man bei einem solchen
   * Streifen. Nach einem Wischen darf es nicht noch einmal ausloesen.
   */
  griff.addEventListener('click', () => {
    if (gezogen) {
      gezogen = false;
      return;
    }
    schliesse();
  });
}

/*
 * Ob gerade ein Text offen steht, traegt das Fenster als Klasse - daran
 * haengt die Sichtbarkeit des Griffs. Frueher las das Stilblatt den Zustand
 * ueber :has(details[open]) ab; die Klasse ist fuer den Browser billiger.
 */
function pflegeOffenesFeld() {
  const fenster = document.querySelector('.dp-kauffenster');
  if (!fenster) return;
  fenster.classList.toggle('dp-kauffenster--offenes-feld', !!fenster.querySelector('details[open]'));
  /*
   * Ist das Feld "Konfiguration" offen, darf das Fenster am Handy hoeher werden (bis 55 dvh statt 38 dvh, solange die 3D-Vorschau
   * steht - Jimmi, Oktober 2026). Das Zuklappen laeuft 380 ms: Erst danach faellt die Marke weg, sonst spraenge das Fenster.
   */
  const konfig = [...fenster.querySelectorAll('details[open]')].some((d) => {
    const kopf = d.querySelector('summary');
    return !!kopf && /Konfiguration/.test(kopf.textContent || '');
  });
  window.clearTimeout(konfigUhr);
  if (konfig) fenster.dataset.dpOffenKonfig = '';
  else if (fenster.hasAttribute('data-dp-offen-konfig')) {
    konfigUhr = window.setTimeout(() => fenster.removeAttribute('data-dp-offen-konfig'), 400);
  }
}

let konfigUhr = 0;

document.addEventListener(
  'toggle',
  (ereignis) => {
    if (ereignis.target instanceof HTMLDetailsElement) pflegeOffenesFeld();
  },
  true
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', pflegeOffenesFeld, { once: true });
} else {
  pflegeOffenesFeld();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', starte, { once: true });
} else {
  starte();
}

/*
 * Baut die Zeile nach, wenn Inhalt erst spaeter eintrifft.
 *
 * Die Felder von IMPRINT ziehen nach dem Laden in das Personalisierungsfeld
 * ein (dp-veredelung.js) - beim ersten Aufbau war es noch leer, und ein
 * leeres Feld bekommt zu Recht keinen Reiter. Sobald mehr gefuellte Felder da
 * sind als Knoepfe in der Zeile, wird die Zeile frisch gebaut.
 */
const nachzuegler = new MutationObserver(() => {
  const fenster = document.querySelector(FENSTER);
  if (!fenster) return;

  const lauf = fenster.querySelector('.dp-kauffenster__lauf') || fenster;
  const felder = [...lauf.querySelectorAll('details')].filter(hatInhalt);
  const knoepfe = fenster.querySelectorAll('.dp-reiter__knopf').length;
  if (felder.length <= knoepfe) return;

  const alt = fenster.querySelector('.dp-reiter');
  if (alt) alt.remove();
  fenster.classList.remove(BEREIT);
  baue();
});

nachzuegler.observe(document.documentElement, { childList: true, subtree: true });

